<script>
import {useThemeStore} from "@js/store/ThemeStore.js";
import {locations} from "@js/map_points.ts";
import {checkDocument} from "@js/helpers.ts";
import {contacts} from "@js/company_details.ts";

export default {
    name: "MapComponent",

    data() {
        return {
            _observer: null,
            _map: null,
            _scriptLoading: null,
            _inited: false,
            _onKeyDown: null,
            _onKeyUp: null,

            center: [55.462540, 37.781900],
            zoom: 11,
        };
    },

    mounted() {
        if (!checkDocument()) return;

        const el = this.$refs.map;

        const tryInit = () => {
            const r = el.getBoundingClientRect();
            if (r.width > 0 && r.height > 0) {
                this._observer && this._observer.disconnect();
                this._observer = null;
                this.loadMap();
                return true;
            }
            return false;
        };

        this._observer = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && tryInit()),
            { threshold: 0.1 }
        );
        this._observer.observe(el);

        setTimeout(() => { if (!this._inited) tryInit(); }, 1500);
    },

    beforeUnmount() {
        if (this._observer) { this._observer.disconnect(); this._observer = null; }
        if (this._map?.destroy) { this._map.destroy(); this._map = null; }
        if (this._onKeyDown) document.removeEventListener('keydown', this._onKeyDown);
        if (this._onKeyUp) document.removeEventListener('keyup', this._onKeyUp);
    },

    methods: {
        async loadMap() {
            if (this._inited) return;
            this._inited = true;

            if (!this._scriptLoading) this._scriptLoading = this._loadYandexMapsScript();
            await this._scriptLoading;
            await window.ymaps.ready();

            const el = this.$refs.map;
            const rect = el.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) {
                await new Promise(r => setTimeout(r, 50));
            }

            const map = new ymaps.Map(el, {
                center: this.center,
                zoom: this.zoom,
                controls: ['zoomControl'],
            }, { suppressMapOpenBlock: true });

            // По умолчанию — запрещаем скролл-зум
            map.behaviors.disable('scrollZoom');
            this._setupCtrlScrollZoom(map);

            // Главный маркер
            const mainPlacemark = new ymaps.Placemark(
                this.center, {}, {
                    iconLayout: 'default#image',
                    iconImageHref: '/assets/map/general_icon.webp',
                    iconImageSize: [32, 32],
                    iconImageOffset: [-16, -32],
                    cursor: 'pointer',
                }
            );
            mainPlacemark.events.add('click', () => {
                const url = contacts?.yandex_map_link;
                if (url) window.open(url, '_blank');
            });
            map.geoObjects.add(mainPlacemark);

            // Остальные точки (валидируем координаты)
            (Array.isArray(locations) ? locations : []).forEach((p) => {
                const lat = Number(p?.lat), lng = Number(p?.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

                const pm = new ymaps.Placemark(
                    [lat, lng],
                    { balloonContent: p?.title || '', hintContent: p?.hint || '' },
                    p?.icon
                        ? { iconLayout: 'default#image', iconImageHref: p.icon, iconImageSize: [24, 24], iconImageOffset: [-12, -24] }
                        : { preset: 'islands#blueDotIcon' }
                );
                map.geoObjects.add(pm);
            });

            // Центр/зум оставляем фиксированными
            map.container.fitToViewport();

            this._map = map;
        },

        // ВКЛЮЧАЕМ scrollZoom только пока зажат Ctrl/Cmd. Без таймеров и без попыток успеть "в тот же wheel".
        _setupCtrlScrollZoom(map) {
            const scrollBehavior = map.behaviors.get('scrollZoom');

            // на всякий случай чутка «смягчим» поведение
            if (scrollBehavior?.options) {
                // можно подкрутить чувствительность, если надо:
                // scrollBehavior.options.set('zoomStep', 1);
                // scrollBehavior.options.set('useKinetic', false);
            }

            // wheel без Ctrl — просто блокируем прокрутку страницы над картой
            map.events.add('wheel', (e) => {
                const oe = e.get('originalEvent');
                const ctrl = !!(oe && (oe.ctrlKey || oe.metaKey));
                if (!ctrl) {
                    // оставляем scrollZoom выключенным и предотвращаем скролл страницы
                    if (oe?.preventDefault) oe.preventDefault();
                }
                // Если Ctrl зажат — ничего не делаем тут: scrollZoom уже включится по keydown
            });

            // глобальные keydown/keyup: это надёжно ловится даже если карта не в фокусе
            this._onKeyDown = (ev) => {
                if (ev.ctrlKey || ev.metaKey) {
                    map.behaviors.enable('scrollZoom');
                }
            };
            this._onKeyUp = (ev) => {
                // Если Ctrl/Cmd отпущен — сразу выключаем
                if (!ev.ctrlKey && !ev.metaKey) {
                    map.behaviors.disable('scrollZoom');
                }
            };
            document.addEventListener('keydown', this._onKeyDown);
            document.addEventListener('keyup', this._onKeyUp);

            // если пользователь зажал Ctrl ДО загрузки карты — проверим текущее состояние
            if (window.event && (window.event.ctrlKey || window.event.metaKey)) {
                map.behaviors.enable('scrollZoom');
            }
        },

        _loadYandexMapsScript() {
            if (window.ymaps?.ready) return Promise.resolve();

            const exist = document.querySelector('script[data-ymaps="true"]');
            if (exist) {
                return new Promise((resolve, reject) => {
                    exist.addEventListener('load', resolve, { once: true });
                    exist.addEventListener('error', reject, { once: true });
                });
            }

            const apiKey = import.meta.env.VITE_YANDEX_GEOCODER_API_KEY || '';
            const src = `https://api-maps.yandex.ru/2.1/?lang=ru_RU${apiKey ? `&apikey=${apiKey}` : ''}`;

            return new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = src;
                s.async = true;
                s.defer = true;
                s.dataset.ymaps = 'true';
                s.onload = resolve;
                s.onerror = reject;
                document.head.appendChild(s);
            });
        },
    },

    setup() {
        const themeStore = useThemeStore();
        return { themeStore, contacts };
    },
};
</script>

<template>
    <div class="map-container w-full h-[450px]" ref="map"></div>
</template>

<style scoped lang="scss">
</style>
